import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Text, textVariantClasses } from './Text';
import { CalendarIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import clsx from 'clsx';
import { Calendar } from './Calendar';
import { ComponentProps, useState } from 'react';
import { DateRange, DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface DatePickerProps {
  label?: string;
  placeholder?: string;
  inputDisabled?: boolean;
  error?: boolean | string;
  width?: number;
  mode?: 'single' | 'multiple' | 'range';
  value?: Date | Date[] | DateRange;
  onChange?: (date: Date | Date[] | DateRange | undefined) => void;
}

export function DatePicker({
  label,
  placeholder,
  inputDisabled,
  error,
  width,
  mode = 'single',
  value,
  onChange,
  ...props
}: DatePickerProps & Omit<ComponentProps<typeof DayPicker>, 'mode'>) {
  const [internalDate, setInternalDate] = useState<Date | Date[] | DateRange>();
  const date = value !== undefined ? value : internalDate;

  const formatSelectedDate = (selectedDate: Date | Date[] | DateRange | undefined): string => {
    if (!selectedDate) return '';

    if (Array.isArray(selectedDate)) {
      if (selectedDate.length === 0) {
        return '';
      }
      if (selectedDate.length === 1) {
        return format(selectedDate[0], 'dd/MM/yyyy', { locale: ptBR });
      }
      if (selectedDate.length <= 2) {
        return selectedDate.map((d) => format(d, 'dd/MM/yyyy', { locale: ptBR })).join(', ');
      }

      return `${format(selectedDate[0], 'dd/MM/yyyy', { locale: ptBR })} e mais ${selectedDate.length - 1}`;
    }

    if (typeof selectedDate === 'object' && 'from' in selectedDate) {
      const { from, to } = selectedDate;

      if (!from) {
        return '';
      }
      if (!to) {
        return `De ${format(from, 'dd/MM/yyyy', { locale: ptBR })}`;
      }

      return `${format(from, 'dd/MM/yyyy', { locale: ptBR })} - ${format(to, 'dd/MM/yyyy', { locale: ptBR })}`;
    }

    return format(selectedDate as Date, 'dd/MM/yyyy', { locale: ptBR });
  };

  const displayText = formatSelectedDate(date) || placeholder;

  return (
    <PopoverPrimitive.Root>
      <div className="w-full" style={{ width }}>
        {label && (
          <Text as="label" variant="label" strong className="mb-2xs block">
            {label}
          </Text>
        )}
        <PopoverPrimitive.Trigger asChild>
          <button
            data-placeholder={!date}
            className={clsx(
              'border-transparent-dark-16 data-[placeholder="true"]:text-neutral-muted focus-visible:border-neutral-default data-[state=open]:border-neutral-default outline-neutral-subtle gap-2xs bg-base-white pl-xs pr-2xs py-2xs flex h-[38px] w-full items-center justify-between rounded-md border whitespace-nowrap shadow-xs transition-all focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:outline-2 data-[state=open]:outline-offset-2',
              error && '!border-error-default !text-error-default',
              textVariantClasses['label-sm-default']
            )}
            disabled={inputDisabled}
            style={{ width }}
          >
            <div className="gap-2xs flex items-center">
              <CalendarIcon
                size={20}
                className={clsx('text-neutral-muted', error && '!text-error-default')}
              />
              {displayText}
            </div>
            {value ? (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.(undefined);
                }}
                className="hover:bg-neutral-subtle rounded-full transition-all outline-none"
              >
                <XIcon size={16} className="text-neutral-muted" />
              </span>
            ) : (
              <ChevronDownIcon size={16} className="text-neutral-muted" />
            )}
          </button>
        </PopoverPrimitive.Trigger>
        {typeof error === 'string' && error.length > 0 && (
          <Text as="span" variant="label" size="xs" className="text-error-default">
            {error}
          </Text>
        )}
      </div>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className="bg-base-white data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 border-transparent-dark-16 z-50 origin-(--radix-popover-content-transform-origin) rounded-md border shadow-xs outline-hidden"
        >
          <Calendar
            {...(props as any)}
            mode={mode as any}
            selected={date}
            onSelect={(newDate: any) => {
              if (onChange) {
                onChange(newDate);
              } else {
                setInternalDate(newDate);
              }
            }}
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
